import React, { useState, useMemo, useCallback, useEffect } from "react"
import { Row, Col, Form, Button } from "react-bootstrap"
import { useNavigate, Link } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import useAuthAxios from "../hooks/useAuthAxios"
import Layout from "../components/layout/Layout"
import { filterEmptyFields } from "../utils/searchUtils"
import { usePersistedState } from "../hooks/usePersistedState"
import DataTable from "../components/common/DataTable"

const UsersSearch = () => {
  const navigate = useNavigate()
  const { appLookups, isAdmin } = useAuth()
  const { get } = useAuthAxios()

  const userRoles = useMemo(() => {
    return appLookups?.filter(lookup => lookup.Table === "UsersRoles") || []
  }, [appLookups])

  const [hasSearched, setHasSearched] = useState(false)
  const [searchCriteria, setSearchCriteria] = usePersistedState("userSearchCriteria", {
    UserID: "",
    RoleID: "",
    Firstname: "",
    LastName: "",
    Active: "1"
  })
  const [searchResults, setSearchResults] = usePersistedState("users-searchResults", [])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)

  useEffect(() => {
    return () => {
      const nextPath = window.location.pathname

      if (!nextPath.startsWith("/users")) {
        // Clear React state
        setSearchCriteria({
          UserID: "",
          RoleID: "",
          Firstname: "",
          LastName: "",
          Active: "1"
        })
        setSearchResults([])

        // Clear localStorage
        localStorage.removeItem("users")
        localStorage.removeItem("userSearchCriteria")
        localStorage.removeItem("users-searchResults")
      }
    }
  }, [setSearchCriteria, setSearchResults])

  const handleReset = useCallback(() => {
    setSearchCriteria({
      UserID: "",
      RoleID: "",
      Firstname: "",
      LastName: "",
      Active: "1"
    })
    setSearchResults([])
    setSearchError(null)
    setHasSearched(false)
  }, [setSearchCriteria, setSearchResults])

  const handleSearch = async e => {
    if (e) {
      e.preventDefault()
    }
    setSearching(true)
    setSearchError(null)
    setSearchResults([])
    setHasSearched(true)

    try {
      const filteredCriteria = filterEmptyFields(searchCriteria)
      const response = await get("/users", { params: filteredCriteria })

      if (response.success && response.data?.Results?.APIuser) {
        setSearchResults(response.data.Results.APIuser)
      } else if (response.data?.Errors?.Problem) {
        setSearchError(response.data.Errors.Problem)
      }
    } catch (error) {
      console.error("Σφάλμα αναζήτησης χρηστών:", error)
      if (error.response?.status !== 404) {
        setSearchError("Αποτυχία αναζήτησης. Παρακαλώ προσπαθήστε ξανά.")
      }
    } finally {
      setSearching(false)
    }
  }

  const handleInputChange = e => {
    const { name, value } = e.target
    setSearchCriteria(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const getRoleDescription = roleId => {
    const role = userRoles.find(r => r.Code.toString() === roleId.toString())
    return role ? role.Name : roleId
  }

  return (
    <Layout>
      <Form onSubmit={handleSearch}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Αναζήτηση Χρηστών</h5>
          {isAdmin() && (
            <Button variant="primary" as={Link} to="/users/new" className="d-flex align-items-center">
              <i className="fas fa-plus me-2" />
              Νέος Χρήστης
            </Button>
          )}
        </div>
        <Row className="g-3">
          <Col lg={2} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Κωδικός Χρήστη</Form.Label>
              <Form.Control
                type="number"
                name="UserID"
                value={searchCriteria.UserID}
                onChange={handleInputChange}
                disabled={searching}
              />
            </Form.Group>
          </Col>
          <Col lg={2} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ρόλος</Form.Label>
              <Form.Select
                name="RoleID"
                value={searchCriteria.RoleID}
                onChange={handleInputChange}
                disabled={searching}
                className="form-control">
                <option value="">Όλοι</option>
                {userRoles.map(role => (
                  <option key={role.Code} value={role.Code.toString()}>
                    {role.Name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Επώνυμο</Form.Label>
              <Form.Control
                type="text"
                name="LastName"
                value={searchCriteria.LastName}
                onChange={handleInputChange}
                disabled={searching}
              />
            </Form.Group>
          </Col>
          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Email</Form.Label>
              <Form.Control
                type="email"
                name="Mail"
                value={searchCriteria.Mail}
                onChange={handleInputChange}
                disabled={searching}
              />
            </Form.Group>
          </Col>
          <Col lg={2} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Κατάσταση</Form.Label>
              <Form.Select
                name="Active"
                value={searchCriteria.Active}
                onChange={handleInputChange}
                disabled={searching}
                className="form-control">
                <option value="1">Ενεργοί</option>
                <option value="0">Ανενεργοί</option>
                <option value="">Όλοι</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col xs={12} className="mt-4">
            <div className="d-flex justify-content-between">
              <Button type="submit" variant="primary" disabled={searching}>
                {searching ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Αναζήτηση...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2" />
                    Αναζήτηση
                  </>
                )}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={searching}>
                <i className="fas fa-undo me-2" />
                Επαναφορά
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <hr className="my-4" />

      {/* Results Section */}
      {searching ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Φόρτωση...</span>
          </div>
        </div>
      ) : searchError ? (
        <div className="text-center py-5 text-danger">
          <i className="fas fa-exclamation-circle fa-2x mb-3 d-block" />
          <p className="mb-0">{searchError}</p>
        </div>
      ) : searchResults.length > 0 ? (
        <DataTable
          data={searchResults}
          loading={searching}
          error={searchError}
          onRowClick={item => navigate(`/users/${item.UserID}`, { state: { userData: item } })}
          columns={[
            {
              header: "Κωδικός Χρήστη",
              field: "UserID",
              type: "string"
            },
            {
              header: "Όνομα Χρήστη",
              field: "LoginUsername",
              type: "string"
            },
            {
              header: "Ρόλος",
              field: "RoleID",
              type: "string",
              render: item => getRoleDescription(item.RoleID)
            },
            {
              header: "Όνομα",
              field: "Firstname",
              type: "string"
            },
            {
              header: "Επώνυμο",
              field: "LastName",
              type: "string"
            },
            {
              header: "Email",
              field: "Mail",
              type: "string"
            },
            {
              header: "Τηλέφωνο",
              field: "Phone",
              type: "string"
            },
            {
              header: "Κατάσταση",
              field: "Active",
              type: "number",
              render: item => (
                <span className={`badge bg-${item.Active ? "success" : "danger"}`}>
                  {item.Active ? "Ενεργός" : "Ανενεργός"}
                </span>
              )
            }
          ]}
        />
      ) : hasSearched ? (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-search fa-2x mb-3 d-block" />
          <p className="mb-0">Δεν βρέθηκαν χρήστες που να αντιστοιχούν στα κριτήρια αναζήτησης.</p>
        </div>
      ) : null}
    </Layout>
  )
}

export default UsersSearch
